import { addDays, format, startOfDay } from 'date-fns';
import { FormCtx } from 'vopay';
import {
  getOrdinalWeekdayByDayCode,
  isWeekdayCode,
  CUSTOM_UNIT_TO_NAME_OF_FREQUENCY,
  NAME_OF_FREQUENCY_TO_CUSTOM_UNIT,
  RecurrenceEndCondition,
  RecurrenceRule,
} from 'UI';
import { ElinxInfo } from './types';

function isToday(dateStr: string): boolean {
  const date = startOfDay(new Date(Number(dateStr)));
  const today = startOfDay(new Date());
  return date.getTime() === today.getTime();
}

export function elinxRuleFromValues(values: ElinxInfo): RecurrenceRule {
  const preset = values.Recurrence || 'one-time';

  // NameOfFrequency defaults to 'monthly' until a preset sets it — only trust it in Custom.
  if (preset !== 'custom') {
    return { preset };
  }

  return {
    preset,
    interval: values.Interval,
    unit: values.NameOfFrequency ? NAME_OF_FREQUENCY_TO_CUSTOM_UNIT[values.NameOfFrequency] : undefined,
    weekdays: values.ByDay ? values.ByDay.split(',').filter(isWeekdayCode) : undefined,
    monthlyRepeatOn: values.NameOfFrequency === 'monthly' && values.ByDay ? 'ordinalWeekday' : 'dayOfMonth',
  };
}

export function elinxEndsFromValues(values: ElinxInfo): RecurrenceEndCondition {
  return values.Ending === 'End Date'
    ? { mode: 'date', date: values.ScheduleEndDate || '' }
    : { mode: 'occurrences', count: values.EndingAfterPayments || '' };
}

export function refreshByDayForStartDate(ctx: FormCtx<ElinxInfo>, startDate: Date): void {
  const rule = elinxRuleFromValues(ctx.values);
  const needsByDayRefresh =
    rule.preset === 'monthly' ||
    (rule.preset === 'custom' && rule.unit === 'month' && rule.monthlyRepeatOn === 'ordinalWeekday');
  if (needsByDayRefresh) {
    ctx.setFieldValue('ByDay', getOrdinalWeekdayByDayCode(startDate));
  }
}

export function applyElinxRuleChange(ctx: FormCtx<ElinxInfo>, rule: RecurrenceRule): void {
  // Switching from one-time to a recurring preset moves ScheduleStartDate from disabled (no
  // date) to active with a minimum of tomorrow — bump it forward if it's still today's default.
  const wasOneTime = !ctx.values.Recurrence || ctx.values.Recurrence === 'one-time';
  let startDate = ctx.values.ScheduleStartDate ? new Date(Number(ctx.values.ScheduleStartDate)) : undefined;
  if (wasOneTime && rule.preset !== 'one-time' && startDate && isToday(ctx.values.ScheduleStartDate)) {
    startDate = addDays(startDate, 1);
    ctx.setFieldValue('ScheduleStartDate', startDate.getTime().toString());
  }

  // Keep ScheduleEndDate strictly after the (possibly just-bumped) start date, or bumping it
  // above can collapse both to the same day.
  if (startDate && ctx.values.ScheduleEndDate) {
    const endDate = new Date(Number(ctx.values.ScheduleEndDate));
    if (format(endDate, 'yyyy-MM-dd') <= format(startDate, 'yyyy-MM-dd')) {
      ctx.setFieldValue('ScheduleEndDate', addDays(startDate, 1).getTime().toString());
    }
  }

  ctx.setFieldValue('Recurrence', rule.preset);

  if (rule.preset === 'one-time') {
    ctx.setFieldValue('Frequency', 'single');
    ctx.setFieldValue('NameOfFrequency', undefined);
    ctx.setFieldValue('Interval', undefined);
    ctx.setFieldValue('ByDay', undefined);
    return;
  }

  ctx.setFieldValue('Frequency', 'recurring');

  if (rule.preset !== 'custom') {
    ctx.setFieldValue('NameOfFrequency', rule.preset);
    ctx.setFieldValue('Interval', undefined);

    if (rule.preset === 'monthly') {
      const startDate = ctx.values.ScheduleStartDate ? new Date(Number(ctx.values.ScheduleStartDate)) : new Date();
      ctx.setFieldValue('ByDay', getOrdinalWeekdayByDayCode(startDate));
    } else {
      ctx.setFieldValue('ByDay', undefined);
    }
    return;
  }

  ctx.setFieldValue('NameOfFrequency', rule.unit ? CUSTOM_UNIT_TO_NAME_OF_FREQUENCY[rule.unit] : undefined);
  ctx.setFieldValue('Interval', rule.interval);

  if (rule.unit === 'week') {
    ctx.setFieldValue('ByDay', (rule.weekdays || []).join(','));
  } else if (rule.unit === 'month' && rule.monthlyRepeatOn === 'ordinalWeekday') {
    const startDate = ctx.values.ScheduleStartDate ? new Date(Number(ctx.values.ScheduleStartDate)) : new Date();
    ctx.setFieldValue('ByDay', getOrdinalWeekdayByDayCode(startDate));
  } else {
    ctx.setFieldValue('ByDay', undefined);
  }
}

export function applyElinxEndsChange(ctx: FormCtx<ElinxInfo>, ends: RecurrenceEndCondition): void {
  if (ends.mode === 'date') {
    ctx.setFieldValue('Ending', 'End Date');
    ctx.setFieldValue('ScheduleEndDate', ends.date);
  } else {
    ctx.setFieldValue('Ending', 'Payments');
    ctx.setFieldValue('EndingAfterPayments', ends.count);
  }
}
